import { SettingsSearchNotFound } from 'App/Map/UserSettingsMenu/components/SettingsSearchNotFound/SettingsSearchNotFound'
import { IconGroups } from 'components/icons/IconGroups'
import { translate } from 'i18n/i18n'
import type { Dispatch, SetStateAction } from 'react'
import { apiFetch } from 'utils/apiFetch'
import { showNotification } from 'utils/showNotification'
import { useIsWideScreen } from 'utils/useIsWideScreen'
import { showConfirmSettingModal } from '../../_utils/showConfirmSettingModal'
import type { NotificationType } from '../_utils/useRelativeNotification'
import { ContactsAndGroupsRow } from '../components/ContactsAndGroupsRow'
import { useSearchGroupsAndContacts } from '../components/useSearchGroupsAndContacts'
import type { Group } from '../ContactsAndGroupsContext'
import { useGroupsAndContacts } from '../ContactsAndGroupsContext'

export const Groups = ({
  onEdit,
  setSelectedGroup,
  showRelativeNotification,
}: {
  onEdit: () => void
  setSelectedGroup: Dispatch<SetStateAction<Group | null>>
  showRelativeNotification: ({ type, message }: { type: NotificationType; message: string }) => void
}) => {
  const isWideScreen = useIsWideScreen()
  const notificationFunctionToUse = isWideScreen ? showRelativeNotification : showNotification
  const { groups: allGroups, deleteGroup } = useGroupsAndContacts()

  const { SearchInputComponent, filteredAndSortedList, isSearchMatch } = useSearchGroupsAndContacts({
    initialList: allGroups,
    placeholder: translate.phrases.banyanApp('Search Groups'),
  })

  const handleDeleteGroup = async (groupId: number) => {
    try {
      await apiFetch({
        url: '/groups-management',
        body: {
          groupsToDelete: [Number(groupId)],
        },
      })

      notificationFunctionToUse({
        type: 'success',
        message: translate.phrases.banyanApp('Group successfully deleted'),
      })

      const deletedGroupId = groupId

      deleteGroup(deletedGroupId)
    } catch (error) {
      notificationFunctionToUse({
        type: 'error',
        message: translate.phrases.banyanApp('Failed to delete group'),
      })
    }
  }

  const onDelete = (groupId: number, groupName: string) => {
    showConfirmSettingModal({
      title: translate.phrases.banyanApp('Delete Group'),
      content: (
        <div>
          <p>
            {translate.phrases.banyanApp('Are you sure you want to delete {{groupName}} as a group?', {
              groupName,
            })}
          </p>
        </div>
      ),
      onConfirm: () => handleDeleteGroup(groupId),
      confirmButtonText: translate.phrases.banyanApp('Delete Group'),
    })
  }

  return (
    <>
      {SearchInputComponent}
      {isSearchMatch ? (
        (filteredAndSortedList as Group[]).map((group) => {
          return (
            <ContactsAndGroupsRow
              id={group.id}
              key={group.id}
              icon={<IconGroups />}
              name={group.name}
              onEdit={() => {
                onEdit()

                setSelectedGroup(group)
              }}
              onDelete={onDelete}
              badgeNumber={group.contacts.length}
            />
          )
        })
      ) : (
        <SettingsSearchNotFound />
      )}
    </>
  )
}
